import { ChatbotContext, Scope } from './types';

export const addUserMessage = ({ context, event }: Scope): Partial<ChatbotContext> => {
  if (event.type !== 'ON_SEND_MESSAGE') return {};

  return {
    messages: [
      ...context.messages,
      {
        text: event.payload.message,
        isUser: true,
        timestamp: new Date(),
      },
    ],
    inputValue: '',
    isLoading: false,
  };
};

export const addBotMessage = ({ context, event }: Scope): Partial<ChatbotContext> => {
  if (event.type !== 'ON_RECEIVE_MESSAGE') return {};

  // Drop late-arriving messages from a prior (pre-reset) session so a
  // stale answer cannot bleed into the new conversation and cannot
  // revert sessionId back to the old value (which would make later
  // REST fallback requests rejoin the wrong history).
  //
  // Only guard when both ids are set AND disagree: on a brand-new
  // session, context.sessionId is still undefined and the server's
  // first response legitimately seeds it. chatbot.reset_confirmed
  // flows through chatbot-session-update → ON_SEND_SHARED_CONTEXT,
  // so it never reaches this reducer.
  if (
    event.payload.sessionId &&
    context.sessionId &&
    event.payload.sessionId !== context.sessionId
  ) {
    console.debug(
      `[chatbot] dropping stale message: event.sessionId=${event.payload.sessionId} active=${context.sessionId}`
    );
    return { isLoading: false, isTyping: false };
  }

  const message = event.payload.message;
  const citations = event.payload.citations || [];
  const suggestedTopics = event.payload.suggestedTopics || [];
  const isClarification = event.payload.isClarification || false;
  const isPii = event.payload.isPii || false;
  const isRateLimited = event.payload.isRateLimited || false;
  const messages = [...context.messages];

  messages.push({
    text: message,
    isUser: false,
    timestamp: new Date(),
    citations,
    suggestedTopics,
    isClarification,
    isPii,
    isRateLimited,
  });

  // Capture server-issued session id (WebSocket responses) so REST fallback
  // can keep the same conversation. Only overwrite when server sent one.
  const update: Partial<ChatbotContext> = {
    messages,
    isLoading: false,
    isTyping: false,
  };
  if (event.payload.sessionId) {
    update.sessionId = event.payload.sessionId;
  }
  return update;
};

export const setTypingStatus = ({ context, event }: Scope): Partial<ChatbotContext> => {
  if (event.type !== 'ON_TYPING_STATUS') return {};

  return {
    isTyping: event.payload.status === 'started',
  };
};

export const updateInput = ({ context, event }: Scope): Partial<ChatbotContext> => {
  if (event.type !== 'ON_INPUT_CHANGE') return {};
  return { inputValue: event.payload.value };
};

export const setLoading = ({ context }: { context: ChatbotContext }): Partial<ChatbotContext> => ({
  isLoading: true,
});

export const clearError = ({ context }: { context: ChatbotContext }): Partial<ChatbotContext> => ({
  error: null,
});

export const closeSocket = ({ context }: { context: ChatbotContext }): Partial<ChatbotContext> => {
  if (context.socket) {
    try {
      context.socket.close();
    } catch (error) {
      console.error('Error closing WebSocket:', error);
    }
  }
  return { socket: null };
};

export const resetConversation = ({
  context,
}: {
  context: ChatbotContext;
}): Partial<ChatbotContext> => ({
  messages: [],
  inputValue: '',
  isLoading: false,
  isTyping: false,
  error: null,
  // Rotate the session id so the next request starts a fresh history.
  // On WebSocket, the server will also rotate and send its new session_id
  // back via chatbot.reset_confirmed; that response will overwrite this
  // local value. For REST-only sessions this generated UUID is authoritative.
  sessionId: crypto.randomUUID(),
});
